import type {
  BootstrapResult,
  LevelAssessmentModel,
  ModelAxis,
  StructuredRubricFields,
} from "@shared/labs/level-assessment-model";
import { MODEL_ID_REGEX } from "@shared/labs/level-assessment-model";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useRoute } from "wouter";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { rpcClient } from "@/lib/rpc";
import { C, DM, MONO, UI } from "../results/ui-tokens";
import {
  BootstrapProgress,
  type BootstrapScopeSpec,
  isBootstrapFullSuccess,
  scopesFromModel,
} from "./bootstrap-progress";
import {
  type AxisDraft,
  type CellMap,
  CompetencyMatrix,
  emptyAxis,
  type FieldsMap,
} from "./competency-matrix";
import { DisciplineCsvImport } from "./discipline-csv-import";
import {
  buildAxisPayload,
  buildRubricCells,
  buildRubricFields,
  cellMapFromAxisCells,
  countEmptyCells,
  dropLevelFromCells,
  dropLevelFromFields,
  fieldsMapFromAxisFields,
  levelsArray,
  removeAxisReindex,
  removeAxisReindexFields,
  setCellIn,
} from "./matrix-cells";
import { MatrixFileImport } from "./matrix-file-import";
import { modelImportToDraft, parseModelMatrixFile } from "./matrix-import";

/**
 * Wizard to create a new evaluation model from scratch.
 *
 * A model has **cross competencies** (scored for every candidate) plus zero or
 * more **disciplines**, each owning **vertical competencies** scored only when
 * a run targets that discipline. Each block is a (axes × levels) matrix whose
 * cells are the per-(axis, level) rubric descriptions. The level range is
 * shared model-wide. Axis/discipline ids are derived server-side from labels.
 *
 * Validation is local; the backend re-validates everything.
 */

interface DisciplineDraft {
  /** Existing discipline id (edit mode) — preserved on save. Undefined for a
   *  newly-added discipline (the backend derives the id from the label). */
  id?: string;
  label: string;
  description: string;
  axes: AxisDraft[];
  cells: CellMap;
  /** Structured Pillar 2/3/4 fields, parallel to `axes` by index. */
  fields: FieldsMap;
  /** Optional continuous sub-band of the model range this function spans.
   *  Undefined = the full model range. */
  levels?: { min: number; max: number };
}

function emptyDiscipline(): DisciplineDraft {
  return { label: "", description: "", axes: [emptyAxis()], cells: {}, fields: {} };
}

/** Map a persisted `ModelAxis` to the editor's `AxisDraft`, carrying the
 *  optional per-axis pillars (only when present, so a blob-only axis stays a
 *  bare {label,description,weight}). */
function axisDraftFromModelAxis(a: ModelAxis): AxisDraft {
  return {
    label: a.label,
    description: a.description,
    weight: a.weight ?? 1,
    ...(a.notAbout != null ? { notAbout: a.notAbout } : {}),
    ...(a.baseline != null ? { baseline: a.baseline } : {}),
    ...(a.falsePositives != null ? { falsePositives: a.falsePositives } : {}),
    ...(a.hardCap != null ? { hardCap: a.hardCap } : {}),
    ...(a.cautionaryProfiles != null ? { cautionaryProfiles: a.cautionaryProfiles } : {}),
  };
}

function slugForModelId(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Make a slug unique against the ids already in use by appending `-2`, `-3`, …
 *  on collision. Keeps ids human-readable (derived from the full name) while
 *  guaranteeing no clash with an existing model — the backend's PK is the final
 *  guard, but this avoids the common case (many models sharing a name prefix). */
function uniqueModelId(base: string, taken: Set<string>): string {
  if (!base || !taken.has(base)) return base;
  for (let n = 2; n <= taken.size + 1; n++) {
    const candidate = `${base}-${n}`;
    if (!taken.has(candidate)) return candidate;
  }
  return `${base}-${taken.size + 2}`;
}

export default function ModelsNewPage({
  onCreated,
  stepIndicator,
}: {
  /** Workflow mode (the create→calibrate shell). When set, instead of showing
   *  the standalone post-create success card, the created model is handed back
   *  so the shell can flow straight into calibration. The disciplines section
   *  is also hidden: the new-model journey creates a **cross-only baseline**;
   *  disciplines are added later from the edit screen ("Adicionar disciplina"). */
  onCreated?: (model: LevelAssessmentModel) => void;
  /** Optional node rendered at the top of the form — the unified workflow
   *  step indicator (Matriz · Perfis · Validação · Aprendizado). */
  stepIndicator?: React.ReactNode;
} = {}) {
  const { toast } = useToast();
  const [, navigate] = useLocation();

  // ── Form state ────────────────────────────────────────────────────
  const [id, setId] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  // Whether the derived id pill is in its inline-edit state. Create-mode only —
  // in edit mode the id is immutable and the pill stays locked.
  const [slugEditing, setSlugEditing] = useState(false);
  // True once the user edits the id by hand — stops it from re-deriving off the
  // name. Until then the id tracks the FULL name (not just the first letter).
  const [slugManual, setSlugManual] = useState(false);
  // Ids already in use, so the auto-derived id can be kept unique (create mode).
  const [existingIds, setExistingIds] = useState<Set<string>>(new Set());
  const [levelsMin, setLevelsMin] = useState(0);
  const [levelsMax, setLevelsMax] = useState(7);
  // Model-wide custom level names, keyed by level number as a string. Carried
  // across cross + every discipline. Empty/absent → the level shows as "L{n}".
  const [levelNames, setLevelNames] = useState<Record<string, string>>({});

  // Cross competencies (model-wide).
  const [crossAxes, setCrossAxes] = useState<AxisDraft[]>([emptyAxis()]);
  const [crossCells, setCrossCells] = useState<CellMap>({});
  const [crossFields, setCrossFields] = useState<FieldsMap>({});

  // Disciplines (vertical competencies).
  const [disciplines, setDisciplines] = useState<DisciplineDraft[]>([]);
  const [showCsvImport, setShowCsvImport] = useState(false);

  // Edit mode: same component, routed at `/models/:modelId/edit`.
  const [isEditRoute, editParams] = useRoute("/labs/level-assessment/models/:modelId/edit");
  const editModelId = isEditRoute ? (editParams?.modelId ?? null) : null;
  const [loadingModel, setLoadingModel] = useState<boolean>(isEditRoute);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!editModelId) return;
    let cancelled = false;
    setLoadingModel(true);
    setLoadError(null);
    (async () => {
      try {
        const [modelRes, rubricsRes] = await Promise.all([
          rpcClient.labs.levelAssessmentModelGet({ modelId: editModelId }) as Promise<{
            model: LevelAssessmentModel;
          }>,
          rpcClient.labs.levelAssessmentModelRubricsGet({ modelId: editModelId }) as Promise<{
            crossCells: Record<string, Record<number, string>>;
            disciplineCells: Record<string, Record<string, Record<number, string>>>;
            crossFields: Record<string, StructuredRubricFields>;
            disciplineFields: Record<string, Record<string, StructuredRubricFields>>;
          }>,
        ]);
        if (cancelled) return;
        const m = modelRes.model;
        setId(m.id);
        // The id is immutable in edit mode — pin it so the name never re-derives it.
        setSlugManual(true);
        setName(m.name);
        setDescription(m.description);
        setLevelsMin(m.levels.min);
        setLevelsMax(m.levels.max);
        setLevelNames(m.levels.names ?? {});
        setCrossAxes(m.crossAxes.map(axisDraftFromModelAxis));
        setCrossCells(cellMapFromAxisCells(m.crossAxes, rubricsRes.crossCells));
        setCrossFields(fieldsMapFromAxisFields(m.crossAxes, rubricsRes.crossFields));
        setDisciplines(
          m.disciplines.map((d) => ({
            id: d.id,
            label: d.label,
            description: d.description,
            axes: d.axes.map(axisDraftFromModelAxis),
            cells: cellMapFromAxisCells(d.axes, rubricsRes.disciplineCells[d.id]),
            fields: fieldsMapFromAxisFields(d.axes, rubricsRes.disciplineFields[d.id]),
            levels: d.levels,
          })),
        );
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : "Erro ao carregar o modelo.");
      } finally {
        if (!cancelled) setLoadingModel(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editModelId]);

  // Create mode: load the ids already in use so the auto-derived id can be kept
  // unique. Non-fatal if it fails — the backend still rejects a true duplicate.
  useEffect(() => {
    if (editModelId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = (await rpcClient.labs.levelAssessmentModelsList({})) as {
          models: { id: string }[];
        };
        if (!cancelled) setExistingIds(new Set(res.models.map((m) => m.id)));
      } catch {
        // Best-effort: derive from the full name and let the backend guard dups.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editModelId]);

  // Derive the id from the FULL model name (kept unique against existing models)
  // until the user edits it by hand. This replaces the old "first keystroke
  // only" sync that collapsed every model to its first letter.
  useEffect(() => {
    if (editModelId || slugManual) return;
    setId(uniqueModelId(slugForModelId(name), existingIds));
  }, [editModelId, slugManual, name, existingIds]);

  // ── Submit state ──────────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [bootstrapResult, setBootstrapResult] = useState<BootstrapResult | null>(null);
  const [createdModel, setCreatedModel] = useState<LevelAssessmentModel | null>(null);
  const [startingCalib, setStartingCalib] = useState(false);

  /** Launch the guided calibration workflow for the just-created model. A
   *  model with disciplines must be calibrated per-discipline (cross-only eval
   *  isn't valid), so send the user to the list's discipline picker; a
   *  cross-only model starts a whole-model session directly. */
  async function startCalibration(model: LevelAssessmentModel) {
    if (model.disciplines.length > 0) {
      toast({
        title: "Escolha a disciplina",
        description: "Na lista de modelos, clique em Calibrar e selecione a disciplina.",
      });
      navigate("/labs/level-assessment/models");
      return;
    }
    setStartingCalib(true);
    try {
      const res = (await rpcClient.labs.levelAssessmentCalibrationSessionCreate({
        modelId: model.id,
        disciplineId: null,
        kind: "new_model",
      })) as { session: { id: string } };
      navigate(`/labs/level-assessment/calibrate/${res.session.id}`);
    } catch (e) {
      toast({
        title: "Falha ao iniciar calibração",
        description: e instanceof Error ? e.message : "Erro inesperado",
        variant: "destructive",
      });
    } finally {
      setStartingCalib(false);
    }
  }

  const levels = useMemo(() => levelsArray(levelsMin, levelsMax), [levelsMin, levelsMax]);

  // ── Validation ────────────────────────────────────────────────────
  const validation = useMemo(() => {
    const issues: string[] = [];
    if (!MODEL_ID_REGEX.test(id)) {
      issues.push('Id do modelo deve ser slug minúsculo. Ex: "acme-engineering".');
    }
    if (name.trim().length === 0) issues.push("Nome do modelo obrigatório.");
    if (crossAxes.length === 0) issues.push("Pelo menos uma competência cross.");
    for (let i = 0; i < crossAxes.length; i++) {
      if (crossAxes[i].label.trim().length === 0) {
        issues.push(`Cross — eixo #${i + 1}: label obrigatório.`);
      }
      if (!(crossAxes[i].weight > 0) || !Number.isFinite(crossAxes[i].weight)) {
        issues.push(`Cross — eixo #${i + 1}: peso deve ser um número maior que zero.`);
      }
    }
    for (let d = 0; d < disciplines.length; d++) {
      const disc = disciplines[d];
      const dn = disc.label.trim() || `#${d + 1}`;
      if (disc.label.trim().length === 0) issues.push(`Disciplina ${dn}: nome obrigatório.`);
      if (disc.axes.length === 0) issues.push(`Disciplina ${dn}: pelo menos um eixo vertical.`);
      for (let i = 0; i < disc.axes.length; i++) {
        if (disc.axes[i].label.trim().length === 0) {
          issues.push(`Disciplina ${dn} — eixo #${i + 1}: label obrigatório.`);
        }
        if (!(disc.axes[i].weight > 0) || !Number.isFinite(disc.axes[i].weight)) {
          issues.push(`Disciplina ${dn} — eixo #${i + 1}: peso deve ser um número maior que zero.`);
        }
      }
    }
    if (levelsMax <= levelsMin) issues.push("L máx precisa ser maior que L mín.");
    if (levelsMin < 0) issues.push("L mín não pode ser negativo.");
    return { issues, valid: issues.length === 0 };
  }, [id, name, crossAxes, disciplines, levelsMin, levelsMax]);

  // ── MVP completeness (non-blocking guidance) ──────────────────────
  // The bootstrap meta-prompts want the "minimum viable set" for a coherent
  // cold-start prompt: a construct sentence + baseline per axis, a description
  // per in-band level, and a "crosses-when" boundary per adjacent pair (see
  // docs/labs/level-assessment/competency-matrix-fields.md). These are surfaced
  // as recommendations — they do NOT block the save (legacy models predate
  // baseline/boundaries).
  const recommendations = useMemo(() => {
    const recs: string[] = [];
    const scan = (
      scopeLabel: string,
      scopeAxes: AxisDraft[],
      scopeCells: CellMap,
      scopeFields: FieldsMap,
      scopeLevels: number[],
    ) => {
      scopeAxes.forEach((a, i) => {
        const an = a.label.trim() || `eixo #${i + 1}`;
        if (!a.description.trim()) recs.push(`${scopeLabel} · ${an}: defina o que o eixo mede.`);
        if (a.baseline == null) recs.push(`${scopeLabel} · ${an}: defina o nível-base.`);
        const missingDef = scopeLevels.filter((l) => !scopeCells[i]?.[l]?.trim()).length;
        if (missingDef > 0) {
          recs.push(`${scopeLabel} · ${an}: ${missingDef} nível(is) sem descrição.`);
        }
        const boundaries = scopeFields[i]?.boundaries ?? {};
        let missingB = 0;
        for (let bi = 0; bi < scopeLevels.length - 1; bi++) {
          if (!boundaries[String(scopeLevels[bi])]?.trim()) missingB++;
        }
        if (missingB > 0) {
          recs.push(`${scopeLabel} · ${an}: ${missingB} fronteira(s) "cruza quando" vazia(s).`);
        }
      });
    };
    scan("Cross", crossAxes, crossCells, crossFields, levels);
    for (const d of disciplines) {
      // Clamp the discipline's band to the current model range (inline, to keep
      // this memo independent of the component-body band helper).
      const clamp = (n: number) => Math.min(levelsMax, Math.max(levelsMin, n));
      const bmin = clamp(d.levels?.min ?? levelsMin);
      const bmax = clamp(d.levels?.max ?? levelsMax);
      scan(
        d.label.trim() || "Disciplina",
        d.axes,
        d.cells,
        d.fields,
        levelsArray(Math.min(bmin, bmax), Math.max(bmin, bmax)),
      );
    }
    return recs;
  }, [crossAxes, crossCells, crossFields, disciplines, levels, levelsMin, levelsMax]);

  // ── Cross axis/cell ops ───────────────────────────────────────────
  function updateCrossAxis(idx: number, patch: Partial<AxisDraft>) {
    setCrossAxes((prev) => prev.map((a, i) => (i === idx ? { ...a, ...patch } : a)));
  }
  function addCrossAxis() {
    setCrossAxes((prev) => [...prev, emptyAxis()]);
  }
  function removeCrossAxis(idx: number) {
    setCrossAxes((prev) => prev.filter((_, i) => i !== idx));
    setCrossCells((prev) => removeAxisReindex(prev, idx));
    setCrossFields((prev) => removeAxisReindexFields(prev, idx));
  }
  function setCrossAxisFields(axisIdx: number, next: StructuredRubricFields) {
    setCrossFields((prev) => ({ ...prev, [axisIdx]: next }));
  }

  /** Apply an uploaded model matrix file (JSON/YAML): replaces the cross block +
   *  levels, and fills name/description when the file supplies them. Returns a
   *  human-readable error to display, or `null` on success. */
  function applyModelFile(text: string): string | null {
    const res = parseModelMatrixFile(text);
    if (!res.ok) return res.error;
    const d = modelImportToDraft(res.data);
    if (d.name != null) setName(d.name);
    if (d.description != null) setDescription(d.description);
    setLevelsMin(d.levelsMin);
    setLevelsMax(d.levelsMax);
    setLevelNames(d.levelNames);
    setCrossAxes(d.crossAxes);
    setCrossCells(d.crossCells);
    setCrossFields(d.crossFields);
    return null;
  }

  // ── Discipline ops ────────────────────────────────────────────────
  function addDiscipline() {
    setDisciplines((prev) => [...prev, emptyDiscipline()]);
  }
  function removeDiscipline(di: number) {
    setDisciplines((prev) => prev.filter((_, i) => i !== di));
  }
  function patchDiscipline(di: number, patch: Partial<DisciplineDraft>) {
    setDisciplines((prev) => prev.map((d, i) => (i === di ? { ...d, ...patch } : d)));
  }
  function updateDisciplineAxis(di: number, idx: number, patch: Partial<AxisDraft>) {
    setDisciplines((prev) =>
      prev.map((d, i) =>
        i === di ? { ...d, axes: d.axes.map((a, j) => (j === idx ? { ...a, ...patch } : a)) } : d,
      ),
    );
  }
  function addDisciplineAxis(di: number) {
    setDisciplines((prev) =>
      prev.map((d, i) => (i === di ? { ...d, axes: [...d.axes, emptyAxis()] } : d)),
    );
  }
  function removeDisciplineAxis(di: number, idx: number) {
    setDisciplines((prev) =>
      prev.map((d, i) =>
        i === di
          ? {
              ...d,
              axes: d.axes.filter((_, j) => j !== idx),
              cells: removeAxisReindex(d.cells, idx),
              fields: removeAxisReindexFields(d.fields, idx),
            }
          : d,
      ),
    );
  }
  function setDisciplineCell(di: number, axisIdx: number, level: number, value: string) {
    setDisciplines((prev) =>
      prev.map((d, i) =>
        i === di ? { ...d, cells: setCellIn(d.cells, axisIdx, level, value) } : d,
      ),
    );
  }
  function setDisciplineAxisFields(di: number, axisIdx: number, next: StructuredRubricFields) {
    setDisciplines((prev) =>
      prev.map((d, i) => (i === di ? { ...d, fields: { ...d.fields, [axisIdx]: next } } : d)),
    );
  }

  // ── Level ops (model-wide — affect cross + every discipline) ──────
  function addLevelAbove() {
    if (levelsMin <= 0) return;
    setLevelsMin(levelsMin - 1);
  }
  function addLevelBelow() {
    setLevelsMax(levelsMax + 1);
  }
  function removeLevel(level: number) {
    if (level === levelsMin && levelsMin < levelsMax) setLevelsMin(levelsMin + 1);
    else if (level === levelsMax && levelsMax > levelsMin) setLevelsMax(levelsMax - 1);
    setCrossCells((prev) => dropLevelFromCells(prev, level));
    setCrossFields((prev) => dropLevelFromFields(prev, level));
    setDisciplines((prev) =>
      prev.map((d) => ({
        ...d,
        cells: dropLevelFromCells(d.cells, level),
        fields: dropLevelFromFields(d.fields, level),
      })),
    );
    setLevelNames((prev) => {
      const next = { ...prev };
      delete next[String(level)];
      return next;
    });
  }

  function setLevelName(level: number, value: string) {
    setLevelNames((prev) => ({ ...prev, [String(level)]: value }));
  }

  /** A discipline's effective level band, clamped to the current model range
   *  (operator level edits can leave a stored band partly out of range). */
  function effectiveDisciplineBand(disc: DisciplineDraft): { min: number; max: number } {
    const c = (n: number) => Math.min(levelsMax, Math.max(levelsMin, n));
    const min = c(disc.levels?.min ?? levelsMin);
    const max = c(disc.levels?.max ?? levelsMax);
    return { min: Math.min(min, max), max: Math.max(min, max) };
  }

  /** Model-wide level names limited to the current range, with blanks dropped —
   *  the shape the backend persists (undefined when nothing is named). */
  function cleanLevelNames(): Record<string, string> | undefined {
    const out: Record<string, string> = {};
    for (let l = levelsMin; l <= levelsMax; l++) {
      const v = levelNames[String(l)]?.trim();
      if (v) out[String(l)] = v;
    }
    return Object.keys(out).length > 0 ? out : undefined;
  }

  // ── Submit ────────────────────────────────────────────────────────
  async function submit() {
    if (!validation.valid || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    setBootstrapResult(null);

    try {
      const crossPayload = crossAxes.map(buildAxisPayload);
      const disciplinesPayload = disciplines.map((d) => {
        const band = effectiveDisciplineBand(d);
        const isFull = band.min === levelsMin && band.max === levelsMax;
        // Only the in-band cells/fields are sent; the backend auto-fills
        // out-of-band levels with "below/above this function" text.
        const bandLevels = levelsArray(band.min, band.max);
        return {
          ...(d.id ? { id: d.id } : {}),
          label: d.label.trim(),
          description: d.description.trim(),
          axes: d.axes.map(buildAxisPayload),
          rubricCells: buildRubricCells(d.axes, d.cells, bandLevels),
          rubricFields: buildRubricFields(d.axes, d.fields, bandLevels),
          // Omit when the function spans the full range (no sub-band).
          ...(isFull ? {} : { levels: band }),
        };
      });
      const crossRubricCells = buildRubricCells(crossAxes, crossCells, levels);
      const crossRubricFields = buildRubricFields(crossAxes, crossFields, levels);
      const names = cleanLevelNames();
      const levelsPayload = { min: levelsMin, max: levelsMax, ...(names ? { names } : {}) };

      const res = (
        editModelId
          ? await rpcClient.labs.levelAssessmentModelUpdate({
              modelId: editModelId,
              name: name.trim(),
              description: description.trim(),
              levels: levelsPayload,
              crossAxes: crossPayload,
              crossRubricCells,
              crossRubricFields,
              disciplines: disciplinesPayload,
              bootstrap: true,
            })
          : await rpcClient.labs.levelAssessmentModelCreate({
              id,
              name: name.trim(),
              description: description.trim(),
              levels: levelsPayload,
              crossAxes: crossPayload,
              crossRubricCells,
              crossRubricFields,
              disciplines: disciplinesPayload,
              bootstrap: true,
            })
      ) as { model: LevelAssessmentModel; bootstrap: BootstrapResult | null };
      const savedVerb = editModelId ? "atualizado" : "criado";
      // A partial edit bootstrap only includes the scopes that changed; check
      // success against just those. `null` means nothing structural changed.
      const bootstrappedKeys = Object.keys(res.bootstrap?.scopes ?? {});
      const fullSuccess =
        res.bootstrap === null
          ? true
          : isBootstrapFullSuccess(
              res.bootstrap,
              scopesFromModel(res.model).filter((s) => bootstrappedKeys.includes(s.key)),
            );
      toast({
        title:
          res.bootstrap === null
            ? `Modelo ${savedVerb}`
            : fullSuccess
              ? `Modelo ${savedVerb} e bootstrapado`
              : `Modelo ${savedVerb}, bootstrap parcial`,
        description:
          res.bootstrap === null
            ? "Sem mudanças estruturais — nada pra re-bootstrapar."
            : fullSuccess
              ? `${res.model.name} pronto pra uso.`
              : "Alguns steps falharam — veja detalhes abaixo. Re-bootstrap disponível na lista.",
        variant: fullSuccess ? "default" : "destructive",
      });
      // Workflow mode: hand the model to the shell (which creates a calibration
      // session and continues the journey). Standalone mode: show the success
      // card with the post-create actions.
      if (onCreated) {
        onCreated(res.model);
      } else {
        setCreatedModel(res.model);
        setBootstrapResult(res.bootstrap);
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : "Erro inesperado";
      setSubmitError(message);
      toast({ title: "Falha ao criar modelo", description: message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  }

  // ── Edit-mode loading / error guards ──────────────────────────────
  if (editModelId && loadingModel) {
    return (
      <div className="ci-stripe ci-stripe-page">
        <div className="ci-narrow fade-up" style={{ width: "100%", maxWidth: 760 }}>
          <div style={{ ...UI, color: C.ink50, fontSize: 14 }}>Carregando modelo…</div>
        </div>
      </div>
    );
  }
  if (editModelId && loadError) {
    return (
      <div className="ci-stripe ci-stripe-page">
        <div className="ci-narrow fade-up" style={{ width: "100%", maxWidth: 760 }}>
          <div
            style={{
              ...UI,
              border: `1px solid ${C.border}`,
              borderRadius: 16,
              background: C.white,
              padding: "14px 18px",
              fontSize: 13.5,
              color: C.redHover,
            }}
          >
            Falha ao carregar o modelo: {loadError}
          </div>
          <div style={{ marginTop: 14 }}>
            <Link href="/labs/level-assessment/models" style={{ textDecoration: "none" }}>
              <GhostButton as="span">Voltar pra lista</GhostButton>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ── Post-success view ────────────────────────────────────────────
  if (createdModel) {
    return (
      <div className="ci-stripe ci-stripe-page">
        <div className="ci-narrow fade-up" style={{ width: "100%", maxWidth: 760 }}>
          <h1
            style={{
              ...DM,
              margin: "0 0 8px",
              fontSize: 32,
              fontWeight: 700,
              color: C.ink,
              letterSpacing: "-0.02em",
            }}
          >
            {createdModel.name}
          </h1>
          <p
            style={{
              ...UI,
              margin: "0 0 22px",
              fontSize: 14,
              color: C.ink70,
              lineHeight: 1.55,
            }}
          >
            Modelo <code style={{ ...MONO, fontSize: 13, color: C.ink }}>{createdModel.id}</code>{" "}
            {editModelId ? "atualizado" : "criado"} com {createdModel.crossAxes.length}{" "}
            competência(s) cross
            {createdModel.disciplines.length > 0
              ? ` e ${createdModel.disciplines.length} disciplina(s)`
              : ""}
            .
          </p>
          <div
            style={{
              border: `1px solid ${C.border}`,
              borderRadius: 16,
              background: C.white,
              padding: 22,
              marginBottom: 18,
            }}
          >
            {bootstrapResult ? (
              <BootstrapProgress
                running={false}
                result={bootstrapResult}
                // Show only the scopes that were (re)bootstrapped — a partial
                // edit only touches the changed scopes.
                scopes={scopesFromModel(createdModel).filter((s) =>
                  Object.keys(bootstrapResult.scopes).includes(s.key),
                )}
              />
            ) : (
              <div style={{ ...UI, fontSize: 13.5, color: C.ink70, lineHeight: 1.55 }}>
                Sem mudanças estruturais — o bootstrap existente foi mantido.
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <SolidButton
              onClick={() => startCalibration(createdModel)}
              disabled={startingCalib}
              testId="models-new-calibrate"
            >
              {startingCalib ? "Abrindo…" : "Calibrar agora"}
            </SolidButton>
            <GhostButton
              onClick={() => navigate("/labs/level-assessment/models")}
              testId="models-new-done"
            >
              Voltar pra lista
            </GhostButton>
            <Link href="/labs/level-assessment" style={{ textDecoration: "none" }}>
              <GhostButton as="span">Ir pra avaliação</GhostButton>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ── Form ─────────────────────────────────────────────────────────
  // 1 matrix + 1 prompt per axis, per scope (cross + each discipline).
  const totalCalls =
    1 + crossAxes.length + disciplines.reduce((acc, d) => acc + 1 + d.axes.length, 0);

  const inFlightScopes: BootstrapScopeSpec[] = [
    {
      key: "cross",
      label: "Cross",
      axes: crossAxes.map((a, i) => ({ id: `axis-${i}`, label: a.label || `Eixo ${i + 1}` })),
    },
    ...disciplines.map((d, di) => ({
      key: `disc-${di}`,
      label: d.label || `Disciplina ${di + 1}`,
      axes: d.axes.map((a, i) => ({ id: `axis-${i}`, label: a.label || `Eixo ${i + 1}` })),
    })),
  ];

  return (
    <div className="ci-stripe ci-stripe-page">
      <div className="ci-narrow fade-up" style={{ width: "100%", maxWidth: 1200 }}>
        <Link
          href="/labs/level-assessment/models"
          style={{
            ...UI,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 12,
            color: C.ink50,
            textDecoration: "none",
            letterSpacing: "0",
            textTransform: "uppercase",
            fontWeight: 600,
          }}
        >
          <BackArrow />
          Voltar pra lista de modelos
        </Link>
        <h1
          style={{
            ...DM,
            margin: "12px 0 8px",
            fontSize: 32,
            fontWeight: 700,
            color: C.ink,
            letterSpacing: "-0.02em",
          }}
        >
          {editModelId ? "Editar modelo de avaliação" : "Novo modelo de avaliação"}
        </h1>
        <p
          style={{
            ...UI,
            margin: "0 0 24px",
            fontSize: 14,
            color: C.ink70,
            lineHeight: 1.55,
            maxWidth: 760,
          }}
        >
          Defina identidade, níveis, competências <strong style={{ color: C.ink }}>cross</strong>{" "}
          (avaliadas para todo candidato) e, opcionalmente,{" "}
          <strong style={{ color: C.ink }}>disciplinas</strong> com competências{" "}
          <strong style={{ color: C.ink }}>verticais</strong>. Cada célula da matriz é o que{" "}
          <em>esse eixo</em> nesse <em>nível</em> exige — entra direto no prompt. O bootstrap gera a
          signal matrix e os prompts por escopo.
          {editModelId
            ? " Ao salvar, o modelo é re-bootstrapado pra refletir a estrutura editada (a calibração é preservada)."
            : ""}
        </p>

        {stepIndicator && <div style={{ marginBottom: 24 }}>{stepIndicator}</div>}

        {/* 1. Identidade */}
        <section data-testid="section-identity" style={{ ...cardStyle, marginBottom: 24 }}>
          <SectionHead n={1} title="Identidade" />
          <div style={{ display: "flex", flexDirection: "column", gap: 18, maxWidth: 680 }}>
            <div>
              <FieldLabel>Nome do modelo</FieldLabel>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Avaliação de engenharia da Acme"
                disabled={submitting}
                data-testid="model-name-input"
                style={{
                  ...DM,
                  height: "auto",
                  width: "100%",
                  padding: "12px 14px",
                  borderRadius: 10,
                  border: `1px solid ${C.ink10}`,
                  background: C.white,
                  fontSize: 18,
                  fontWeight: 500,
                  color: C.ink,
                }}
              />
              {/* Derived id — rendered as a pill. Locked in edit mode (immutable);
                  editable on create via the "editar id" affordance. */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginTop: 10,
                  flexWrap: "wrap",
                }}
              >
                {slugEditing && !editModelId ? (
                  <>
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        background: C.white,
                        border: `1px solid ${C.red}`,
                        borderRadius: 8,
                        padding: "5px 11px",
                      }}
                    >
                      <span
                        style={{
                          ...UI,
                          fontSize: 11,
                          fontWeight: 600,
                          letterSpacing: "0.04em",
                          textTransform: "uppercase",
                          color: C.ink50,
                        }}
                      >
                        id
                      </span>
                      <Input
                        value={id}
                        onChange={(e) => {
                          setId(slugForModelId(e.target.value));
                          setSlugManual(true);
                        }}
                        placeholder="meu-modelo"
                        disabled={submitting}
                        data-testid="model-id-input"
                        style={{
                          ...MONO,
                          height: "auto",
                          width: 240,
                          border: 0,
                          padding: 0,
                          background: "transparent",
                          fontSize: 13,
                          color: C.ink,
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setSlugEditing(false)}
                      style={{
                        ...UI,
                        border: 0,
                        background: "transparent",
                        cursor: "pointer",
                        fontSize: 12.5,
                        fontWeight: 600,
                        color: C.ink50,
                        padding: "4px 0",
                      }}
                    >
                      concluir
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setSlugManual(false);
                        setSlugEditing(false);
                      }}
                      style={{
                        ...UI,
                        border: 0,
                        background: "transparent",
                        cursor: "pointer",
                        fontSize: 12.5,
                        fontWeight: 600,
                        color: C.ink50,
                        padding: "4px 0",
                      }}
                    >
                      voltar ao automático
                    </button>
                  </>
                ) : (
                  <>
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        background: C.paper,
                        border: `1px solid ${C.border}`,
                        borderRadius: 8,
                        padding: "6px 11px",
                      }}
                    >
                      <span
                        style={{
                          ...UI,
                          fontSize: 11,
                          fontWeight: 600,
                          letterSpacing: "0.04em",
                          textTransform: "uppercase",
                          color: C.ink50,
                        }}
                      >
                        id
                      </span>
                      {editModelId ? (
                        // Edit mode: the id is immutable. Keep it as a real
                        // (disabled) input so `model-id-input` stays addressable.
                        <input
                          type="text"
                          value={id}
                          readOnly
                          disabled
                          data-testid="model-id-input"
                          style={{
                            ...MONO,
                            border: 0,
                            padding: 0,
                            background: "transparent",
                            fontSize: 13,
                            color: id ? C.ink : C.ink50,
                            width: Math.max(40, id.length * 8),
                          }}
                        />
                      ) : (
                        <span style={{ ...MONO, fontSize: 13, color: id ? C.ink : C.ink50 }}>
                          {id || "—"}
                        </span>
                      )}
                    </div>
                    {editModelId ? (
                      <span style={{ ...UI, fontSize: 12, color: C.ink50 }}>O id é imutável.</span>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => setSlugEditing(true)}
                          disabled={submitting}
                          style={{
                            ...UI,
                            border: 0,
                            background: "transparent",
                            cursor: submitting ? "default" : "pointer",
                            fontSize: 12.5,
                            fontWeight: 600,
                            color: C.red,
                            padding: "4px 0",
                          }}
                        >
                          editar id
                        </button>
                        <span style={{ ...UI, fontSize: 12, color: C.ink50 }}>
                          gerado a partir do nome · imutável depois de criado
                        </span>
                      </>
                    )}
                    {/* Keep the editable id field mounted (hidden) when locked so
                        the data-testid stays addressable and slug-from-name keeps
                        flowing into it. In edit mode the id never changes. */}
                    {!editModelId && (
                      <input
                        type="text"
                        value={id}
                        onChange={(e) => {
                          setId(slugForModelId(e.target.value));
                          setSlugManual(true);
                        }}
                        disabled={submitting}
                        data-testid="model-id-input"
                        aria-hidden="true"
                        tabIndex={-1}
                        style={{
                          position: "absolute",
                          width: 1,
                          height: 1,
                          padding: 0,
                          margin: -1,
                          overflow: "hidden",
                          clip: "rect(0 0 0 0)",
                          border: 0,
                        }}
                      />
                    )}
                  </>
                )}
              </div>
            </div>
            <div>
              <FieldLabel hint="uma linha, pra que esse modelo serve">Descrição</FieldLabel>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                placeholder="Ex: Foco em deep-tech, senioridade L0–L7."
                disabled={submitting}
                data-testid="model-description-input"
                style={{
                  ...DM,
                  width: "100%",
                  minHeight: "auto",
                  padding: "11px 14px",
                  borderRadius: 10,
                  border: `1px solid ${C.ink10}`,
                  background: C.white,
                  fontSize: 15,
                  color: C.ink,
                }}
              />
            </div>
          </div>
        </section>

        {/* 2. Níveis (shared model-wide) */}
        <section data-testid="section-levels" style={{ ...cardStyle, marginBottom: 24 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              marginBottom: 6,
            }}
          >
            <SectionHead n={2} title="Níveis" />
            <span style={{ ...MONO, fontSize: 12, color: C.ink50 }}>
              {levels.length} níveis · L{levelsMin} → L{levelsMax}
            </span>
          </div>
          <p
            style={{
              ...UI,
              margin: "0 0 16px",
              fontSize: 13,
              lineHeight: 1.5,
              color: C.ink70,
              maxWidth: 640,
            }}
          >
            O range de níveis (L{levelsMin}–L{levelsMax}) é compartilhado por todas as matrizes —
            cross e verticais. Os rótulos renumeram sozinhos; o nome é opcional e aparece nos
            prompts e relatórios.
          </p>
          {/* Level cards — one per level, edge-only removal preserved. The
              add-above / add-below affordances keep their existing guards. */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "stretch" }}>
            <AddLevelCard
              onClick={addLevelAbove}
              disabled={levelsMin <= 0 || submitting}
              label={`+ L${levelsMin - 1}`}
              sub="acima"
              testId="matrix-add-level-above"
            />
            {levels.map((level) => {
              const isEdge = level === levelsMin || level === levelsMax;
              const canRemove = isEdge && levelsMin < levelsMax;
              return (
                <div
                  key={level}
                  style={{
                    position: "relative",
                    flex: "none",
                    width: 158,
                    background: C.paper,
                    border: `1px solid ${C.border}`,
                    borderRadius: 12,
                    padding: "12px 13px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 9,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <span style={{ ...DM, fontWeight: 700, fontSize: 15, color: C.ink }}>
                      L{level}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeLevel(level)}
                      disabled={!canRemove || submitting}
                      data-testid={`matrix-remove-level-${level}`}
                      title={
                        !isEdge
                          ? "Só níveis nas pontas podem ser removidos"
                          : !canRemove
                            ? "Pelo menos um nível precisa existir"
                            : `Remover L${level}`
                      }
                      style={{
                        border: 0,
                        background: "transparent",
                        cursor: canRemove ? "pointer" : "default",
                        opacity: canRemove ? 1 : 0.3,
                        color: canRemove ? C.violet : C.ink50,
                        display: "flex",
                        alignItems: "center",
                        padding: 0,
                      }}
                    >
                      <CloseIcon />
                    </button>
                  </div>
                  <Input
                    value={levelNames[String(level)] ?? ""}
                    onChange={(e) => setLevelName(level, e.target.value)}
                    placeholder="Ex: Sênior"
                    disabled={submitting}
                    data-testid={`level-name-${level}`}
                    style={{
                      ...DM,
                      height: "auto",
                      width: "100%",
                      padding: "7px 9px",
                      borderRadius: 7,
                      border: `1px solid ${C.ink10}`,
                      background: C.white,
                      fontSize: 13,
                      color: C.ink,
                    }}
                  />
                </div>
              );
            })}
            <AddLevelCard
              onClick={addLevelBelow}
              disabled={submitting}
              label={`+ L${levelsMax + 1}`}
              sub="abaixo"
              testId="matrix-add-level-below"
            />
          </div>
        </section>

        {/* 3. Competências Cross */}
        <section data-testid="section-cross" style={{ marginBottom: 24 }}>
          <SectionHead n={3} title="Competências Cross" />
          <p style={{ ...UI, margin: "8px 0 16px", fontSize: 13, color: C.ink70, maxWidth: 760 }}>
            Avaliadas para <strong style={{ color: C.ink }}>todo</strong> candidato, independente de
            disciplina. Escolha um eixo e preencha nível a nível.
          </p>
          <MatrixFileImport
            testidPrefix="model"
            disabled={submitting}
            hint="Carrega níveis + competências cross de um arquivo (name, description, levels, crossAxes)."
            successMessage="Matriz cross importada."
            onApply={applyModelFile}
          />
          <CompetencyMatrix
            axes={crossAxes}
            cells={crossCells}
            fields={crossFields}
            levels={levels}
            levelNames={levelNames}
            disabled={submitting}
            testidPrefix="cross"
            onAddAxis={addCrossAxis}
            onRemoveAxis={removeCrossAxis}
            onUpdateAxis={updateCrossAxis}
            onSetCell={(ai, lvl, v) => setCrossCells((prev) => setCellIn(prev, ai, lvl, v))}
            onSetFields={setCrossAxisFields}
          />
        </section>

        {/* 4. Disciplinas (vertical competencies) — hidden in the new-model
            workflow (cross-only baseline); managed from the edit screen. */}
        {!onCreated && (
          <section data-testid="section-disciplines" style={{ marginBottom: 24 }}>
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 12,
                marginBottom: 12,
                flexWrap: "wrap",
              }}
            >
              <div>
                <SectionHead n={4} title="Disciplinas" />
                <p
                  style={{ ...UI, margin: "8px 0 0", fontSize: 13, color: C.ink70, maxWidth: 640 }}
                >
                  Opcional. Cada disciplina tem suas competências verticais, avaliadas só quando a
                  avaliação roda contra ela. Modelo sem disciplinas roda só com as cross.
                </p>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {editModelId ? (
                  // Adding a discipline runs its own guided create→calibrate
                  // journey (matrix → calibration). The inline blocks below stay
                  // edit-only for existing disciplines.
                  <Link
                    href={`/labs/level-assessment/models/${editModelId}/disciplines/new`}
                    data-testid="add-discipline-workflow"
                    style={{ ...dashedBtnStyle, textDecoration: "none" }}
                  >
                    + Adicionar disciplina
                  </Link>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => setShowCsvImport((v) => !v)}
                      disabled={submitting}
                      data-testid="import-discipline-csv"
                      style={dashedBtnStyle}
                    >
                      {showCsvImport ? "Fechar import CSV" : "Importar de CSV"}
                    </button>
                    <button
                      type="button"
                      onClick={addDiscipline}
                      disabled={submitting}
                      data-testid="add-discipline"
                      style={dashedBtnStyle}
                    >
                      + disciplina
                    </button>
                  </>
                )}
              </div>
            </div>

            {showCsvImport && (
              <DisciplineCsvImport
                levels={levels}
                disabled={submitting}
                onCancel={() => setShowCsvImport(false)}
                onImport={(d) => {
                  // CSV import is def-only; the discipline starts with empty
                  // structured fields (authored afterward in the matrix editor).
                  setDisciplines((prev) => [...prev, { ...d, fields: {} }]);
                  setShowCsvImport(false);
                }}
              />
            )}

            {disciplines.length === 0 && !showCsvImport && (
              <div
                style={{
                  ...UI,
                  border: `1px solid ${C.border}`,
                  borderRadius: 16,
                  background: C.white,
                  padding: "16px 20px",
                  fontSize: 13,
                  color: C.ink70,
                }}
              >
                Nenhuma disciplina — o modelo será cross-only. Adicione uma para definir
                competências verticais.
              </div>
            )}

            {disciplines.map((disc, di) => {
              const band = effectiveDisciplineBand(disc);
              const bandLevels = levelsArray(band.min, band.max);
              const isFullBand = band.min === levelsMin && band.max === levelsMax;
              const emptyCells = countEmptyCells(disc.axes, disc.cells, bandLevels);
              const levelOption = (l: number) => {
                const nm = levelNames[String(l)]?.trim();
                return nm ? `L${l} · ${nm}` : `L${l}`;
              };
              return (
                <div
                  // biome-ignore lint/suspicious/noArrayIndexKey: disciplines are ordered by position.
                  key={di}
                  data-testid={`discipline-block-${di}`}
                  style={{ ...cardStyle, padding: 22, marginBottom: 16 }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 12,
                      marginBottom: 12,
                      flexWrap: "wrap",
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "minmax(200px, 280px) 1fr",
                          gap: 12,
                        }}
                      >
                        <div>
                          <FieldLabel>{`Disciplina #${di + 1} — nome`}</FieldLabel>
                          <Input
                            value={disc.label}
                            onChange={(e) => patchDiscipline(di, { label: e.target.value })}
                            placeholder="Engenharia"
                            disabled={submitting}
                            data-testid={`discipline-label-${di}`}
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <FieldLabel>Descrição</FieldLabel>
                          <Input
                            value={disc.description}
                            onChange={(e) => patchDiscipline(di, { description: e.target.value })}
                            placeholder="Competências técnicas verticais de engenharia."
                            disabled={submitting}
                            data-testid={`discipline-description-${di}`}
                            style={inputStyle}
                          />
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeDiscipline(di)}
                      disabled={submitting}
                      data-testid={`discipline-remove-${di}`}
                      title="Remover esta disciplina"
                      style={{
                        ...DM,
                        flexShrink: 0,
                        border: `1px solid ${C.border}`,
                        background: C.white,
                        borderRadius: 999,
                        padding: "8px 14px",
                        fontSize: 13,
                        fontWeight: 700,
                        color: C.violet,
                        cursor: submitting ? "default" : "pointer",
                        opacity: submitting ? 0.6 : 1,
                      }}
                    >
                      Remover disciplina
                    </button>
                  </div>
                  <div style={{ ...UI, fontSize: 12, color: C.ink70, marginBottom: 10 }}>
                    {disc.axes.length} eixo{disc.axes.length === 1 ? "" : "s"} vertical
                    {disc.axes.length === 1 ? "" : "is"}
                    {emptyCells > 0 && (
                      <>
                        {" · "}
                        <span style={{ color: C.amberFg }}>{emptyCells} vazias</span>
                      </>
                    )}
                  </div>
                  {/* Per-function level sub-band — a continuous subset of the
                    model range (e.g. L3–L6). Levels outside it are auto-marked
                    "below/above this function" in the rubric. */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 12,
                      flexWrap: "wrap",
                    }}
                  >
                    <span style={{ ...UI, fontSize: 12, color: C.ink70 }}>
                      Faixa de níveis desta função:
                    </span>
                    <select
                      value={band.min}
                      disabled={submitting}
                      data-testid={`discipline-level-min-${di}`}
                      onChange={(e) => {
                        const min = Number(e.target.value);
                        patchDiscipline(di, { levels: { min, max: Math.max(min, band.max) } });
                      }}
                      style={bandSelectStyle}
                    >
                      {levels.map((l) => (
                        <option key={l} value={l}>
                          {levelOption(l)}
                        </option>
                      ))}
                    </select>
                    <span style={{ ...UI, fontSize: 12, color: C.ink70 }}>até</span>
                    <select
                      value={band.max}
                      disabled={submitting}
                      data-testid={`discipline-level-max-${di}`}
                      onChange={(e) => {
                        const max = Number(e.target.value);
                        patchDiscipline(di, { levels: { min: Math.min(band.min, max), max } });
                      }}
                      style={bandSelectStyle}
                    >
                      {levels.map((l) => (
                        <option key={l} value={l}>
                          {levelOption(l)}
                        </option>
                      ))}
                    </select>
                    {!isFullBand && (
                      <button
                        type="button"
                        onClick={() => patchDiscipline(di, { levels: undefined })}
                        disabled={submitting}
                        data-testid={`discipline-level-reset-${di}`}
                        style={{
                          ...UI,
                          border: 0,
                          background: "transparent",
                          cursor: submitting ? "default" : "pointer",
                          fontSize: 11.5,
                          fontWeight: 600,
                          color: C.red,
                          padding: "4px 0",
                        }}
                      >
                        usar range completo
                      </button>
                    )}
                  </div>
                  <CompetencyMatrix
                    axes={disc.axes}
                    cells={disc.cells}
                    fields={disc.fields}
                    levels={bandLevels}
                    levelNames={levelNames}
                    disabled={submitting}
                    testidPrefix={`disc-${di}`}
                    onAddAxis={() => addDisciplineAxis(di)}
                    onRemoveAxis={(idx) => removeDisciplineAxis(di, idx)}
                    onUpdateAxis={(idx, patch) => updateDisciplineAxis(di, idx, patch)}
                    onSetCell={(ai, lvl, v) => setDisciplineCell(di, ai, lvl, v)}
                    onSetFields={(ai, next) => setDisciplineAxisFields(di, ai, next)}
                  />
                </div>
              );
            })}
          </section>
        )}

        {/* Validation summary */}
        {!validation.valid && (
          <div
            style={{
              ...UI,
              padding: "16px 20px",
              marginTop: 8,
              background: C.amberBg,
              border: `1px solid ${C.amberFg}`,
              borderRadius: 12,
            }}
            data-testid="validation-issues"
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: C.amberFg,
                textTransform: "uppercase",
                letterSpacing: "0",
                marginBottom: 8,
              }}
            >
              Pendências
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {validation.issues.map((issue) => (
                <div
                  key={issue}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 8,
                    fontSize: 13.5,
                    color: C.amberFg,
                  }}
                >
                  <WarnDot />
                  <span>{issue}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tudo certo — pronto pra bootstrapar. */}
        {validation.valid && (
          <div
            style={{
              ...UI,
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 8,
              fontSize: 13.5,
              fontWeight: 500,
              color: C.green,
            }}
          >
            <CheckIcon />
            Tudo certo — pronto pra bootstrapar.
          </div>
        )}

        {/* Non-blocking guidance toward the MVP set (a stronger cold-start). */}
        {recommendations.length > 0 && (
          <div
            style={{
              ...UI,
              padding: "16px 20px",
              marginTop: 8,
              background: C.white,
              border: `1px solid ${C.border}`,
              borderRadius: 12,
            }}
            data-testid="matrix-recommendations"
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: C.ink50,
                textTransform: "uppercase",
                letterSpacing: "0",
                marginBottom: 4,
              }}
            >
              Recomendações · {recommendations.length}
            </div>
            <div style={{ fontSize: 12, color: C.ink50, marginBottom: 10 }}>
              Opcional, mas melhora o cold-start: dimensão + nível-base por eixo, descrição por
              nível, e a fronteira "cruza quando" entre níveis.
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {recommendations.slice(0, 8).map((rec, i) => (
                <div
                  // biome-ignore lint/suspicious/noArrayIndexKey: derived advisory strings can collide; index keeps the read-only list stable.
                  key={`${i}-${rec}`}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 8,
                    fontSize: 13,
                    color: C.ink70,
                  }}
                >
                  <span style={{ color: C.ink50, flex: "none" }}>·</span>
                  <span>{rec}</span>
                </div>
              ))}
              {recommendations.length > 8 && (
                <div style={{ fontSize: 12, color: C.ink50, paddingLeft: 16 }}>
                  +{recommendations.length - 8} mais.
                </div>
              )}
            </div>
          </div>
        )}

        {submitError && (
          <div
            style={{
              ...UI,
              padding: "14px 18px",
              marginTop: 16,
              border: `1px solid ${C.border}`,
              borderRadius: 12,
              background: C.white,
              color: C.redHover,
              fontSize: 13,
            }}
          >
            {submitError}
          </div>
        )}

        {/* In-flight bootstrap progress */}
        {submitting && (
          <div style={{ ...cardStyle, padding: 22, marginTop: 16 }}>
            <BootstrapProgress running result={null} scopes={inFlightScopes} />
          </div>
        )}

        {/* Sticky bottom action bar */}
        <div
          style={{
            position: "sticky",
            bottom: 0,
            marginTop: 24,
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 14,
            flexWrap: "wrap",
            padding: "14px 0",
            background: "rgba(255,255,255,0.92)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            borderTop: `1px solid ${C.ink10}`,
          }}
        >
          <Link href="/labs/level-assessment/models" style={{ textDecoration: "none" }}>
            <button
              type="button"
              style={{
                ...DM,
                border: 0,
                background: "transparent",
                cursor: "pointer",
                fontSize: 15,
                fontWeight: 700,
                color: C.ink70,
                padding: "10px 16px",
              }}
            >
              Cancelar
            </button>
          </Link>
          <button
            type="button"
            onClick={submit}
            disabled={!validation.valid || submitting}
            data-testid="model-create-submit"
            style={{
              ...DM,
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              border: 0,
              borderRadius: 999,
              padding: "12px 22px",
              cursor: !validation.valid || submitting ? "default" : "pointer",
              fontSize: 15,
              fontWeight: 700,
              color: C.white,
              background: !validation.valid || submitting ? C.red20 : C.red,
              opacity: !validation.valid || submitting ? 0.85 : 1,
              boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
            }}
          >
            {submitting
              ? editModelId
                ? "Salvando + bootstrapando…"
                : "Criando + bootstrapando…"
              : `${editModelId ? "Salvar" : "Criar"} e bootstrapar`}
            {!submitting && (
              <span style={{ ...UI, fontWeight: 500, fontSize: 12, opacity: 0.85 }}>
                ({totalCalls} chamadas Opus, ~30-60s)
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Subcomponents + style constants
// ───────────────────────────────────────────────────────────────────────────

/** Numbered circular section badge + DM Sans heading (the mockup's section
 *  head). The ink-on-white "N" circle mirrors the calibrate WorkflowStepper. */
function SectionHead({ n, title }: { n: number; title: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <span
        style={{
          ...DM,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 24,
          height: 24,
          borderRadius: 999,
          background: C.ink,
          color: C.white,
          fontSize: 13,
          fontWeight: 700,
        }}
      >
        {n}
      </span>
      <h2 style={{ ...DM, margin: 0, fontSize: 19, fontWeight: 700, color: C.ink }}>{title}</h2>
    </div>
  );
}

/** Input/section field label — Inter 13 semibold, with an optional muted hint
 *  rendered inline after the label. */
function FieldLabel({ children, hint }: { children: React.ReactNode; hint?: string }) {
  return (
    <div
      style={{
        ...UI,
        display: "block",
        fontSize: 13,
        fontWeight: 600,
        color: C.ink,
        marginBottom: 7,
      }}
    >
      {children}
      {hint && <span style={{ fontWeight: 400, color: C.ink50 }}> — {hint}</span>}
    </div>
  );
}

/** Dashed "+ level" card matching the mockup's add-level affordance. Honors the
 *  existing add-above / add-below guards via `disabled`. */
function AddLevelCard({
  onClick,
  disabled,
  label,
  sub,
  testId,
}: {
  onClick: () => void;
  disabled: boolean;
  label: string;
  sub: string;
  testId: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      style={{
        ...UI,
        flex: "none",
        width: 158,
        minHeight: 74,
        border: `1px dashed ${C.ink20}`,
        background: "transparent",
        borderRadius: 12,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.4 : 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 3,
        color: C.ink50,
        padding: 12,
      }}
    >
      <span style={{ ...DM, fontSize: 14, fontWeight: 700 }}>{label}</span>
      <span style={{ fontSize: 12, fontWeight: 600 }}>{sub}</span>
    </button>
  );
}

/** Solid pill button (DM Sans, ink fill). Mirrors calibrate's PrimaryButton. */
function SolidButton({
  children,
  onClick,
  disabled = false,
  testId,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  testId?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      style={{
        ...DM,
        fontWeight: 700,
        fontSize: 13,
        padding: "9px 18px",
        borderRadius: 999,
        cursor: disabled ? "not-allowed" : "pointer",
        background: disabled ? C.ink10 : C.ink,
        color: disabled ? C.ink50 : C.white,
        border: `1px solid ${disabled ? C.ink10 : C.ink}`,
        opacity: disabled ? 0.8 : 1,
      }}
    >
      {children}
    </button>
  );
}

/** Outline pill button. Renders as a span (inside a Link) when `as="span"`. */
function GhostButton({
  children,
  onClick,
  testId,
  as = "button",
}: {
  children: React.ReactNode;
  onClick?: () => void;
  testId?: string;
  as?: "button" | "span";
}) {
  const style: React.CSSProperties = {
    ...DM,
    display: "inline-flex",
    alignItems: "center",
    fontWeight: 700,
    fontSize: 13,
    padding: "9px 18px",
    borderRadius: 999,
    cursor: "pointer",
    background: C.white,
    color: C.ink,
    border: `1px solid ${C.ink}`,
  };
  if (as === "span") {
    return (
      <span data-testid={testId} style={style}>
        {children}
      </span>
    );
  }
  return (
    <button type="button" onClick={onClick} data-testid={testId} style={style}>
      {children}
    </button>
  );
}

// ── Inline icons (Material Symbols are NOT loaded; use currentColor SVGs) ────

function BackArrow() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M19 12H5M11 18l-6-6 6-6"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M18 6 6 18M6 6l12 12"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
      <path
        d="M8.5 12.2l2.4 2.4 4.6-5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function WarnDot() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      style={{ flexShrink: 0, marginTop: 1 }}
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
      <path d="M12 7.5v5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <circle cx="12" cy="16.2" r="1.1" fill="currentColor" />
    </svg>
  );
}

// ── Shared token styles ─────────────────────────────────────────────────────

/** White card on the paper canvas — 1px border, radius 16. */
const cardStyle: React.CSSProperties = {
  border: `1px solid ${C.border}`,
  borderRadius: 16,
  background: C.white,
  padding: "24px 26px",
};

/** Standard text input — radius 10, 1px ink-10 border. */
const inputStyle: React.CSSProperties = {
  ...UI,
  height: "auto",
  width: "100%",
  padding: "10px 12px",
  borderRadius: 10,
  border: `1px solid ${C.ink10}`,
  background: C.white,
  fontSize: 14,
  color: C.ink,
};

/** Dashed pill affordance for add/import actions (used by both <button> and
 *  <Link>). Uppercase Inter caption on a dashed border. */
const dashedBtnStyle: React.CSSProperties = {
  ...UI,
  display: "inline-flex",
  alignItems: "center",
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: C.ink50,
  background: "transparent",
  border: `1px dashed ${C.ink20}`,
  borderRadius: 999,
  padding: "6px 14px",
  cursor: "pointer",
};

/** Level-band <select> — radius 10, paper fill. */
const bandSelectStyle: React.CSSProperties = {
  ...UI,
  height: 32,
  fontSize: 12,
  padding: "0 8px",
  borderRadius: 10,
  border: `1px solid ${C.ink10}`,
  background: C.white,
  color: C.ink,
};
